/**
 * Pure render-time grouping for `<ChatThread>` system messages —
 * in-platform-model-view Phase 4 Session 4.
 *
 * The Phase 2 `/model-edit` controller appends `[model-view:${namedRange}]
 * <displayName> changed to <formattedValue> via model view` SystemMessages
 * to the LangGraph `messages` channel. After Phase 4 S4's API filter
 * relaxation, these reach `useConversation.messages[]` with `role: 'system'`.
 *
 * This function takes the message array and replaces sequences of adjacent
 * same-`namedRange` system messages whose timestamps fall within
 * `GROUP_WINDOW_MS` of each other with a single `mv-group` placeholder
 * carrying the underlying `messages[]`. The renderer (`<ModelViewEditGroup>`)
 * then displays them as a collapsed-by-default chronological group —
 * cleaning up the chat noise from rapid-fire edits to the same input.
 *
 * Render-time only — no LangGraph mutation, no API call. The append-only
 * invariant on the `messages` channel stays intact.
 */
import type { ChatMessage } from './ChatThread';

export const MODEL_VIEW_PREFIX_RE = /^\[model-view:([^\]]+)\] /;
/**
 * `[table-edit:<table>] <N> cell(s) applied via table edit` breadcrumb prefix
 * appended by `POST /api/conversations/:id/table-edit` (table-input-ux, 2026-06-08).
 * `<ChatThread>` detects this in the system-message branch and renders a
 * `<TableEditReceipt>` instead of the generic single-line row. These messages
 * are NOT grouped — each applied edit is a distinct point-in-time receipt — so
 * they pass through `groupSystemEdits` as singleton `ChatMessage`s (the
 * model-view grouper's `parseModelViewPrefix` returns null for them).
 */
export const TABLE_EDIT_PREFIX_RE = /^\[table-edit:([^\]]+)\] /;
export const GROUP_WINDOW_MS = 30_000;

export interface ModelViewEditGroup {
  kind: 'mv-group';
  namedRange: string;
  messages: ChatMessage[];
}

export type ChatThreadEntry = ChatMessage | ModelViewEditGroup;

/** Type guard distinguishing aggregate group entries from passthrough messages. */
export function isModelViewEditGroup(
  entry: ChatThreadEntry
): entry is ModelViewEditGroup {
  return (entry as ModelViewEditGroup).kind === 'mv-group';
}

/**
 * Resolve a `ChatMessage`'s timestamp to milliseconds since epoch. Falls
 * back to `Date.now()` when the message has no timestamp metadata —
 * defensive for messages constructed in tests / streaming-state without
 * a server-assigned `created_at`. Production messages have either an
 * upstream `created_at` (LangGraph checkpoint) or were appended within
 * the last second, so the fallback effectively groups recent untimestamped
 * messages with their immediate neighbors.
 */
function timestampMs(msg: ChatMessage): number {
  // Look for a `created_at` field on the message (added at the API
  // boundary when system messages are surfaced). Defensive — ChatMessage's
  // public type doesn't promise it, so cast and check.
  const ts = (msg as ChatMessage & { created_at?: string }).created_at;
  if (typeof ts === 'string') {
    const parsed = Date.parse(ts);
    if (Number.isFinite(parsed)) return parsed;
  }
  return Date.now();
}

/**
 * Match the `[model-view:${namedRange}] ` content prefix and return the
 * named range when present. Returns null for non-model-view system messages
 * + non-system messages (which don't get parsed at all). Exported for unit
 * testing.
 */
export function parseModelViewPrefix(msg: ChatMessage): string | null {
  if (msg.role !== 'system') return null;
  const match = MODEL_VIEW_PREFIX_RE.exec(msg.content);
  return match ? match[1] : null;
}

/**
 * Walk the message list and emit a flat array of either passthrough
 * `ChatMessage`s OR `mv-group` aggregates. Adjacent same-`namedRange`
 * model-view system messages within `GROUP_WINDOW_MS` collapse into one
 * group. Non-system messages and model-view system messages with different
 * `namedRange` or outside the window break the group.
 */
export function groupSystemEdits(
  messages: readonly ChatMessage[]
): ChatThreadEntry[] {
  const out: ChatThreadEntry[] = [];
  let buffer: { namedRange: string; messages: ChatMessage[] } | null = null;

  const flush = () => {
    if (buffer) {
      // Single-message "group" downgrades to the underlying message — no
      // benefit to the collapsed renderer when there's only one entry.
      // (Spec §3 — only collapse when N > 1.)
      if (buffer.messages.length === 1) {
        out.push(buffer.messages[0]);
      } else {
        out.push({
          kind: 'mv-group',
          namedRange: buffer.namedRange,
          messages: buffer.messages,
        });
      }
      buffer = null;
    }
  };

  for (const msg of messages) {
    const namedRange = parseModelViewPrefix(msg);
    if (namedRange !== null) {
      if (
        buffer &&
        buffer.namedRange === namedRange &&
        timestampMs(msg) -
          timestampMs(buffer.messages[buffer.messages.length - 1]) <=
          GROUP_WINDOW_MS
      ) {
        buffer.messages.push(msg);
        continue;
      }
      flush();
      buffer = { namedRange, messages: [msg] };
      continue;
    }
    flush();
    out.push(msg);
  }
  flush();

  return out;
}
